import { CSSProperties, MouseEvent, useEffect, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { format, isValid } from 'date-fns';
import { GripVertical } from 'lucide-react';
import type { Task, TaskStatus } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// ============================================
// GANTT BAR
// ============================================
//
// t17: a single horizontal bar rendered inside a Gantt row. The bar
// has three drag handles — a body drag (move both dates), a left-edge
// drag (resize start date) and a right-edge drag (resize end date).
// The parent timeline owns the dnd-kit DndContext and translates the
// drag delta into a date delta (see gantt-view.tsx onDragEnd).
//
// Visual rules:
//   - Red bar if the task is overdue (status !== DONE, endDate < now)
//   - Greyed bar if status === DONE (low opacity, line-through title)
//   - Other statuses get a colour derived from the status palette.
//
// Layering: the resize handles sit at z-20 on top of the move handle
// (z-10), so the 6px edge slices always win pointer events over the
// body. The move handle is the click target that opens the task
// detail when the user clicks without dragging (dnd-kit's PointerSensor
// with activationConstraint distance lets clicks propagate).

export interface GanttBarProps {
    task: Task;
    /** Pixel offset of the bar's start within the timeline (left edge). */
    left: number;
    /** Pixel width of the bar. >= MIN_BAR_WIDTH so a single-day bar is grabbable. */
    width: number;
    /** The visible time scale — used for the label density only. */
    timeScaleLabel: 'day' | 'week' | 'month' | 'auto';
    /** True when the user can drag the bar to reschedule. */
    canDrag: boolean;
    onClick?: (task: Task) => void;
    /**
     * R3 / t17: true while this bar's geometry is being driven by a
     * live drag preview. The bar gets a slightly stronger shadow +
     * a hair more brightness so the user can tell the size is
     * transient rather than committed yet.
     */
    isPreviewing?: boolean;
}

const MIN_BAR_WIDTH = 10;

const STATUS_PALETTE: Record<TaskStatus, { bg: string; border: string; text: string }> = {
    TODO: { bg: 'bg-indigo-500/70', border: 'border-indigo-400', text: 'text-white' },
    IN_PROGRESS: { bg: 'bg-blue-500/70', border: 'border-blue-400', text: 'text-white' },
    IN_REVIEW: { bg: 'bg-purple-500/70', border: 'border-purple-400', text: 'text-white' },
    DONE: { bg: 'bg-emerald-500/60', border: 'border-emerald-400', text: 'text-white' },
    CANCELLED: { bg: 'bg-gray-500/50', border: 'border-gray-400', text: 'text-white/80' },
};

// Date helpers — the bar shows the start → end dates in the timeline's
// locale (matches the timeline's day/week/month header style).
function formatRange(start: Date | null, end: Date | null): string {
    if (!start || !isValid(start)) return '';
    if (!end || end.getTime() === start.getTime()) {
        return format(start, 'MMM d');
    }
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
}

interface DraggablePartProps {
    task: Task;
    mode: 'move' | 'resize-start' | 'resize-end';
    canDrag: boolean;
    className: string;
    ariaLabel: string;
    onClick?: (task: Task) => void;
    children?: React.ReactNode;
}

function DraggablePart({
    task,
    mode,
    canDrag,
    className,
    ariaLabel,
    onClick,
    children,
}: DraggablePartProps) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `gantt-${mode}-${task.id}`,
        data: { type: 'gantt-bar', mode, taskId: task.id },
        disabled: !canDrag,
    });

    const style: CSSProperties = isDragging ? { opacity: 0.3 } : {};

    // R4 / t22: when drag is enabled we still want a click (no drag)
    // on the bar to open the task-detail side panel. dnd-kit's
    // PointerSensor with activationConstraint.distance=6 doesn't
    // suppress the click event after a drag — instead the drag
    // activation pre-empts the click while a real click falls through.
    //
    // However the move handle sits *inside* the resize handles
    // (z-10 / z-20), so a click on the body needs to fire reliably.
    // We use a ref to track "a drag just ended" so the next click
    // event (if any) is suppressed — dnd-kit dispatches a synthetic
    // pointerup-as-click after a drag in some edge cases.
    const justDraggedRef = useRef(false);
    useEffect(() => {
        if (isDragging) {
            justDraggedRef.current = true;
            return;
        }
        if (justDraggedRef.current) {
            // Drag just ended — reset on a microtask. Any genuine
            // click event queued during the same tick checks this
            // ref synchronously and gets suppressed.
            const id = setTimeout(() => {
                justDraggedRef.current = false;
            }, 0);
            return () => clearTimeout(id);
        }
        return;
    }, [isDragging]);

    const handleClick = onClick
        ? (e: MouseEvent) => {
              if (justDraggedRef.current) {
                  // Drag just ended — suppress the click so we don't
                  // accidentally open the panel after a resize.
                  e.stopPropagation();
                  e.preventDefault();
                  return;
              }
              onClick(task);
          }
        : undefined;

    // When drag is disabled, render a non-interactive div so layout
    // stays correct without dnd-kit's listeners swallowing click
    // events. We still forward onClick so the task detail panel
    // works for read-only viewers.
    if (!canDrag) {
        return (
            <div
                ref={setNodeRef}
                className={className}
                style={style}
                onClick={handleClick}
                aria-label={ariaLabel}
            >
                {children}
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            className={className}
            style={style}
            aria-label={ariaLabel}
            title={ariaLabel}
            {...attributes}
            {...listeners}
            onClick={handleClick}
            data-testid={`gantt-bar-${mode}-${task.id}`}
        >
            {children}
        </div>
    );
}

export function GanttBar({
    task,
    left,
    width,
    timeScaleLabel,
    canDrag,
    onClick,
    isPreviewing = false,
}: GanttBarProps) {
    const { t } = useI18n();

    const startIso = (task as any).startDate as string | null | undefined;
    const endIso = (task as any).endDate as string | null | undefined;
    const start = startIso ? new Date(startIso) : null;
    const end = endIso ? new Date(endIso) : null;
    const hasRange = Boolean(start && end);

    // Visual state: overdue vs done vs normal.
    const isDone = task.status === 'DONE' || task.status === 'CANCELLED';
    const isOverdue =
        !isDone && end && isValid(end) && end.getTime() < Date.now();

    const palette = STATUS_PALETTE[task.status] ?? STATUS_PALETTE.TODO;
    const finalWidth = Math.max(width, MIN_BAR_WIDTH);

    const containerStyle: CSSProperties = {
        left: `${left}px`,
        width: `${finalWidth}px`,
    };

    const dateLabel = formatRange(start, end);

    // Only show the inline title when the bar has enough room.
    // R11 / t39: include day view — a 5-day task at day scale is
    // 120px wide which fits the title comfortably. Without this
    // gate the user sees tooltip-only bars at every time scale.
    // Auto (still resolved by parent) is also included for the
    // same reason.
    const showInlineLabel =
        finalWidth >= 100 &&
        (timeScaleLabel === 'day' ||
            timeScaleLabel === 'week' ||
            timeScaleLabel === 'month');

    const tooltip = hasRange ? `${task.title} (${dateLabel})` : task.title;

    return (
        <div
            className={cn(
                'absolute top-1/2 -translate-y-1/2 h-7 rounded-md border select-none',
                // R3 / t17: bump shadow + add a hairline ring when the
                // bar geometry is a live drag preview so the user
                // sees the size as transient.
                isPreviewing ? 'shadow-md ring-2 ring-primary/40 z-30' : 'shadow-sm',
                isOverdue ? 'bg-red-500/70 border-red-400' : palette.bg,
                palette.border,
                isDone ? 'opacity-60' : '',
                canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
            )}
            style={containerStyle}
            data-task-id={task.id}
            data-previewing={isPreviewing ? 'true' : undefined}
            data-gantt-bar={task.id}
            title={tooltip}
        >
            {/* Left resize handle — 6px slice, z-20 so it wins over the move handle. */}
            <DraggablePart
                task={task}
                mode="resize-start"
                canDrag={canDrag}
                ariaLabel={t('gantt.bar.resizeStartAria')}
                className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize z-20"
            />

            {/* Center "move" handle — full body, z-10. */}
            <DraggablePart
                task={task}
                mode="move"
                canDrag={canDrag}
                ariaLabel={t('gantt.bar.moveAria')}
                onClick={onClick}
                className="absolute inset-0 flex items-center px-2 gap-1 z-10"
            >
                {canDrag && (
                    <GripVertical className="h-3 w-3 opacity-60 shrink-0" />
                )}
                {showInlineLabel ? (
                    <span
                        className={cn(
                            'truncate text-[11px] font-medium pointer-events-none',
                            palette.text,
                            isDone ? 'line-through' : ''
                        )}
                    >
                        {task.title}
                    </span>
                ) : null}
            </DraggablePart>

            {/* Right resize handle — 6px slice, z-20. */}
            <DraggablePart
                task={task}
                mode="resize-end"
                canDrag={canDrag}
                ariaLabel={t('gantt.bar.resizeEndAria')}
                className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize z-20"
            />
        </div>
    );
}
